import { SubstanceVerificationStatus } from '@/api/generated-types';
import { keyBy } from '@/utils/data/collections';
import { choiceTitle } from '@/utils/substance/variantComparison';
import type { SubstancePicks } from '@/utils/substance/substanceSelectionUtils';
import type {
  FormDangerousGoodsItem,
  ImdgSubstanceDetail,
  Nullish,
  RankSubstancesRequest,
} from '@/api/types';
import type { ValidationResult } from '@/stores/validationStore';

type RankingInput = Omit<RankSubstancesRequest, 'substanceIds'>;

export interface VariantContainer {
  ctuId: string;
  label: string;
  position: string | null;
  shippers: string[];
  packagings: string[];
  weight?: number;
  cargoIds: string[];
}

export interface VariantCargo {
  name: string;
  technicalName?: string;
  shipper?: string;
  packaging?: string;
  weight?: number;
}

export interface VariantGroup {
  key: string;
  unNumber: string;
  name: string;
  wordings: { name: string; count: number }[];
  candidateIds: string[];
  allIds: string[];
  ranking: RankingInput;
  cargoIds: string[];
  containers: VariantContainer[];
  cargoDetails: Map<string, VariantCargo>;
  currentByCargoId: Map<string, string>;
}

interface VariantGroupInput {
  dangerousGoods: FormDangerousGoodsItem[];
  extractedGoods: FormDangerousGoodsItem[];
  ctus: ReadonlyMap<string, { label: string; position: string | null }>;
  validationByCargoId: ReadonlyMap<string, ValidationResult>;
  focusCargoId?: string | null;
}

// The BE multipleMatchesVisible predicate that raises "Multiple matches, select a variant". Pass the
// cargo row: a re-verify leaves selectedSubstanceId empty even when the form has a substance.
export function needsVariantPick(
  result: Nullish<ValidationResult>,
  cargo?: Nullish<FormDangerousGoodsItem>
): boolean {
  return (
    result?.validationStatus === SubstanceVerificationStatus.MultipleMatches &&
    !(cargo?.substanceId || result.selectedSubstanceId)
  );
}

// selectedSubstanceId alone doesn't mean the user picked: conversion copies the auto-selection
// into it, and a restore copies the form's substance onto exact matches too.
function pickedByUser(result: ValidationResult): boolean {
  return (
    (result.substanceIds?.length ?? 0) >= 2 &&
    !!result.selectedSubstanceId &&
    result.selectedSubstanceId !== result.autoSelectedSubstanceId
  );
}

function candidateIdsOf(result: ValidationResult): string[] {
  const narrowed = result.substanceIds ?? [];
  return narrowed.length >= 2 ? narrowed : (result.unrefinedSubstanceIds ?? narrowed);
}

export function openVariantPickCount(
  dangerousGoods: FormDangerousGoodsItem[],
  validationByCargoId: ReadonlyMap<string, ValidationResult>
): number {
  return dangerousGoods.filter((cargo) => {
    const result = validationByCargoId.get(cargo.cargoId);
    return !!result && needsVariantPick(result, cargo) && candidateIdsOf(result).length >= 2;
  }).length;
}

const clean = (s: Nullish<string>) => s?.trim() || undefined;

function rankingOf(source: FormDangerousGoodsItem): RankingInput {
  return {
    properShippingName: clean(source.properShippingName),
    hazardClass: clean(source.hazardClass),
    packingGroup: clean(source.packingGroup),
    subsidiaryHazards: [...(source.subsidiaryHazards ?? [])].sort(),
  };
}

function packagingLabel(cargo: FormDangerousGoodsItem): string | undefined {
  const { count, code, description } = cargo.outerPackaging ?? {};
  const kind = clean(description) ?? clean(code);
  if (!kind) return undefined;
  const withCode = clean(code) && kind !== clean(code) ? `${kind} (${clean(code)})` : kind;
  return count && !kind.startsWith(`${count} `) ? `${count} × ${withCode}` : withCode;
}

interface Draft {
  unNumber: string;
  candidateIds: string[];
  allIds: string[];
  cargo: FormDangerousGoodsItem[];
  rankings: Map<string, { value: RankingInput; count: number }>;
  current: Map<string, string>;
}

// Groups are UN + class + PG plus the exact candidate set, so UN1325 PG II and PG III, or two names
// under one UN that Hazcheck offers different variants for, never share a pick.
export function buildVariantGroups({
  dangerousGoods,
  extractedGoods,
  ctus,
  validationByCargoId,
  focusCargoId,
}: VariantGroupInput): VariantGroup[] {
  const extractedById = keyBy(extractedGoods, (e) => e.cargoId);
  const drafts = new Map<string, Draft>();

  for (const cargo of dangerousGoods) {
    const result = validationByCargoId.get(cargo.cargoId);
    if (!result) continue;
    const isFocus = cargo.cargoId === focusCargoId;
    if (!isFocus && !needsVariantPick(result, cargo) && !pickedByUser(result)) continue;

    const candidateIds = candidateIdsOf(result);
    if (candidateIds.length < 2) continue;

    const key = `${result.substanceGroupKey ?? result.unNumber ?? cargo.unNumber}|${[...candidateIds].sort().join(',')}`;
    let draft = drafts.get(key);
    if (!draft) {
      draft = {
        unNumber: result.unNumber || cargo.unNumber || '',
        candidateIds,
        allIds: result.unrefinedSubstanceIds?.length ? result.unrefinedSubstanceIds : candidateIds,
        cargo: [],
        rankings: new Map(),
        current: new Map(),
      };
      drafts.set(key, draft);
    }
    draft.cargo.push(cargo);

    const ranking = rankingOf(extractedById.get(cargo.cargoId) ?? cargo);
    const rankingKey = JSON.stringify(ranking);
    const seen = draft.rankings.get(rankingKey);
    if (seen) seen.count++;
    else draft.rankings.set(rankingKey, { value: ranking, count: 1 });

    const current = cargo.substanceId || result.selectedSubstanceId;
    if (current) draft.current.set(cargo.cargoId, current);
  }

  const groups = [...drafts].map(([key, d]): VariantGroup => {
    const containers = new Map<string, VariantContainer>();
    const wordings = new Map<string, number>();
    const cargoDetails = new Map<string, VariantCargo>();
    for (const cargo of d.cargo) {
      const name = clean(cargo.properShippingName) ?? '';
      const shipper = clean(cargo.dcm?.shipperConsignee);
      const packaging = packagingLabel(cargo);
      const weight = cargo.grossWeight ?? cargo.netWeight ?? undefined;
      wordings.set(name, (wordings.get(name) ?? 0) + 1);
      cargoDetails.set(cargo.cargoId, {
        name,
        technicalName: clean(cargo.technicalName),
        shipper,
        packaging,
        weight,
      });

      let container = containers.get(cargo.ctuId);
      if (!container) {
        container = {
          ctuId: cargo.ctuId,
          label: ctus.get(cargo.ctuId)?.label ?? '',
          position: ctus.get(cargo.ctuId)?.position ?? null,
          shippers: [],
          packagings: [],
          cargoIds: [],
        };
        containers.set(cargo.ctuId, container);
      }
      container.cargoIds.push(cargo.cargoId);
      if (shipper && !container.shippers.includes(shipper)) container.shippers.push(shipper);
      if (packaging && !container.packagings.includes(packaging))
        container.packagings.push(packaging);
      if (weight != null) container.weight = (container.weight ?? 0) + weight;
    }

    const sortedWordings = [...wordings]
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count);
    const ranking = [...d.rankings.values()].reduce((a, b) => (b.count > a.count ? b : a)).value;

    return {
      key,
      unNumber: d.unNumber,
      name: sortedWordings[0]?.name ?? '',
      wordings: sortedWordings,
      candidateIds: d.candidateIds,
      allIds: d.allIds,
      ranking,
      cargoIds: d.cargo.map((c) => c.cargoId),
      containers: [...containers.values()],
      cargoDetails,
      currentByCargoId: d.current,
    };
  });

  const isOpen = (g: VariantGroup) => g.currentByCargoId.size < g.cargoIds.length;
  return groups.sort(
    (a, b) => Number(isOpen(b)) - Number(isOpen(a)) || b.cargoIds.length - a.cargoIds.length
  );
}

export const assignedIn = (picks: SubstancePicks, group: VariantGroup, cargoId: string) =>
  picks.get(cargoId)?.substanceId ?? group.currentByCargoId.get(cargoId);

export const doneCount = (picks: SubstancePicks, group: VariantGroup) =>
  group.cargoIds.filter((id) => assignedIn(picks, group, id)).length;

export function nextOpenGroup(
  groups: readonly VariantGroup[],
  picks: SubstancePicks,
  from: number
): number {
  for (let step = 1; step <= groups.length; step++) {
    const i = (from + step) % groups.length;
    if (doneCount(picks, groups[i]) < groups[i].cargoIds.length) return i;
  }
  return from;
}

/** Picks that differ from the cargo's current variant, which is what Save writes. */
export function changedPicks(
  groups: readonly VariantGroup[],
  picks: SubstancePicks
): Map<string, ImdgSubstanceDetail> {
  const out = new Map<string, ImdgSubstanceDetail>();
  for (const g of groups) {
    for (const id of g.cargoIds) {
      const pick = picks.get(id);
      if (pick?.substanceId && pick.substanceId !== g.currentByCargoId.get(id)) out.set(id, pick);
    }
  }
  return out;
}

interface ChoiceChange {
  title: string;
  cargo: number;
  containers: Map<string, { label: string; cargo: number }>;
  replaced: number;
}

interface GroupChange {
  index: number;
  group: VariantGroup;
  choices: Map<string, ChoiceChange>;
  open: number;
}

export function summariseChanges(
  groups: readonly VariantGroup[],
  picks: SubstancePicks,
  changed: ReadonlyMap<string, ImdgSubstanceDetail>
) {
  const all = groups.map((group, index): GroupChange => {
    const containerOf = new Map(
      group.containers.flatMap((c) => c.cargoIds.map((id) => [id, c] as const))
    );
    const choices = new Map<string, ChoiceChange>();
    for (const id of group.cargoIds) {
      const pick = changed.get(id);
      if (!pick?.substanceId) continue;
      let choice = choices.get(pick.substanceId);
      if (!choice) {
        choice = { title: choiceTitle(pick), cargo: 0, containers: new Map(), replaced: 0 };
        choices.set(pick.substanceId, choice);
      }
      choice.cargo++;
      const container = containerOf.get(id);
      if (container) {
        const seen = choice.containers.get(container.ctuId);
        if (seen) seen.cargo++;
        else choice.containers.set(container.ctuId, { label: container.label, cargo: 1 });
      }
      if (group.currentByCargoId.has(id)) choice.replaced++;
    }
    return { index, group, choices, open: group.cargoIds.length - doneCount(picks, group) };
  });

  const changedGroups = all.filter((g) => g.choices.size);
  const openGroups = all.filter((g) => !g.choices.size && g.open);
  return {
    changedGroups,
    openGroups,
    untouched: all.length - changedGroups.length - openGroups.length,
    openCargo: all.reduce((n, g) => n + g.open, 0),
  };
}
